import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ResponsiveBar } from "@nivo/bar";
import { DateTime } from "luxon";
import styles from "./AdminImport.module.scss";
import { APIService } from "../../shared/shared-with-mobile/api-client/api.service";
import {
  HubspotPlatformStats,
  MatchCandidate,
  MatchGroup,
  ShopifyPlatformStats,
  SyncOverview,
} from "../../shared/shared-with-mobile/api-client/services/admin-analytics.service";
import Button from "../../components/Button/Button";

// ============================================================
// Import & Sync — admin page for managing the cross-platform link
// between TNS license-holders and external CRM data.
//
// Three tabs:
//   - Overview     : per-platform counts + paid-revenue-on-unmatched dollars
//   - Shopify      : stats cards + monthly revenue chart + match review
//   - HubSpot      : stats cards + lifecycle/deal breakdown + match review
//
// Match review is grouped per external record. Each group shows the
// external record (Shopify customer / HubSpot contact) on top, then a
// row of candidate license-holders beneath with match-% pills, the
// signals that fired (team / city / phone / etc.), and per-candidate
// usage stats (questions all-time, total users, date created, last
// coach + player activity) so the admin can pick the right one.
// ============================================================

type Section = "overview" | "shopify" | "hubspot";

const AdminImport: React.FC = () => {
  const [section, setSection] = useState<Section>("overview");
  const [overview, setOverview] = useState<SyncOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const data = await APIService.ADMIN_ANALYTICS.GET_SYNC_OVERVIEW();
      setOverview(data);
    } catch (e) {
      console.error("Failed to load sync overview", e);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Import &amp; Sync</h1>
          <p className={styles.subtitle}>
            Match imported Shopify customers and HubSpot contacts to Team Nation
            license-holders. Approve suggested links to bring richer data into
            each customer profile.
          </p>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 4,
          borderBottom: "1px solid #424258",
          marginBottom: 24,
        }}
      >
        {(["overview", "shopify", "hubspot"] as Section[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSection(s)}
            style={{
              background: "transparent",
              border: "none",
              borderBottom:
                section === s ? "2px solid #ffbc17" : "2px solid transparent",
              color: section === s ? "#ffbc17" : "#ababb7",
              padding: "10px 18px",
              fontSize: 14,
              fontWeight: section === s ? 700 : 500,
              cursor: "pointer",
              textTransform: "capitalize",
              marginBottom: -1,
            }}
          >
            {s === "hubspot" ? "HubSpot" : s}
          </button>
        ))}
      </div>

      {section === "overview" && (
        <OverviewSection
          overview={overview}
          loading={overviewLoading}
          onJumpTo={(s) => setSection(s)}
        />
      )}
      {section === "shopify" && <ShopifyTab onLinkChange={loadOverview} />}
      {section === "hubspot" && <HubspotTab onLinkChange={loadOverview} />}
    </div>
  );
};

// ============================================================
// Overview tab
// ============================================================
const OverviewSection: React.FC<{
  overview: SyncOverview | null;
  loading: boolean;
  onJumpTo: (s: "shopify" | "hubspot") => void;
}> = ({ overview, loading, onJumpTo }) => {
  if (loading) return <div className={styles.card}>Loading overview…</div>;
  if (!overview)
    return (
      <div className={styles.card}>
        <p>Could not load overview. Try refreshing.</p>
      </div>
    );

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div
        className={styles.card}
        style={{ borderColor: "rgba(76,175,80,0.4)" }}
      >
        <h3 className={styles.cardTitle}>
          Shopify — Top Priority (Paid Customers)
        </h3>
        <StatGrid
          stats={[
            ["Total customers", overview.shopify.totalCustomers],
            ["Confirmed links", overview.shopify.confirmed, "#4CAF50"],
            ["Suggested (review)", overview.shopify.suggested, "#FFC107"],
            ["Rejected", overview.shopify.rejected, "#6f6f7e"],
            ["Unmatched", overview.shopify.unmatched, "#ababb7"],
          ]}
        />
        <div
          style={{
            background: "rgba(255,188,23,0.08)",
            border: "1px solid rgba(255,188,23,0.3)",
            borderRadius: 6,
            padding: 12,
            marginTop: 12,
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 12, color: "#ababb7", marginBottom: 4 }}>
            Paid revenue from customers NOT yet linked to a license-holder
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#ffbc17" }}>
            {fmtMoney(overview.shopify.paidUnmatchedRevenue)}
          </div>
        </div>
        <Button onClick={() => onJumpTo("shopify")} theme="primary">
          Open Shopify tab ({overview.shopify.suggested} to review)
        </Button>
      </div>

      <div className={styles.card}>
        <h3 className={styles.cardTitle}>HubSpot</h3>
        <StatGrid
          stats={[
            ["Total contacts", overview.hubspot.totalContacts],
            ["Confirmed links", overview.hubspot.confirmed, "#4CAF50"],
            ["Suggested (review)", overview.hubspot.suggested, "#FFC107"],
            ["Rejected", overview.hubspot.rejected, "#6f6f7e"],
            ["Unmatched", overview.hubspot.unmatched, "#ababb7"],
          ]}
        />
        <div style={{ marginTop: 12 }}>
          <Button onClick={() => onJumpTo("hubspot")} theme="primary">
            Open HubSpot tab ({overview.hubspot.suggested} to review)
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Shopify tab: stats cards, monthly revenue bar chart, match review
// ============================================================
const ShopifyTab: React.FC<{ onLinkChange: () => void }> = ({
  onLinkChange,
}) => {
  const [stats, setStats] = useState<ShopifyPlatformStats | null>(null);
  const [groups, setGroups] = useState<MatchGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g] = await Promise.all([
        APIService.ADMIN_ANALYTICS.GET_SHOPIFY_STATS(),
        APIService.ADMIN_ANALYTICS.GET_SYNC_GROUPS("shopify", 50, 0),
      ]);
      setStats(s);
      setGroups(g.groups);
    } catch (e) {
      console.error("Failed to load Shopify tab", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className={styles.card}>Loading Shopify data…</div>;

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {stats && (
        <>
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Shopify At a Glance</h3>
            <StatGrid
              stats={[
                [
                  "Paying customers",
                  stats.totalPayingCustomers.toLocaleString(),
                ],
                [
                  "Lifetime cash",
                  fmtMoney(stats.lifetimeCashCollected),
                  "#4CAF50",
                ],
                ["Past 12 mo cash", fmtMoney(stats.past12moCash), "#4CAF50"],
                ["Total paid orders", stats.totalPaidOrders.toLocaleString()],
                ["Avg order value", fmtMoney(stats.averageOrderValue)],
              ]}
            />
          </div>

          <div className={styles.card}>
            <h3 className={styles.cardTitle}>
              Cash Collected by Month (paid orders)
            </h3>
            <MonthlyRevenueBar data={stats.monthlyRevenue} />
          </div>
        </>
      )}

      <div className={styles.card}>
        <h3 className={styles.cardTitle}>
          Likely matches awaiting your review ({groups.length})
        </h3>
        <p className={styles.helpText}>
          Each row is a Shopify customer with one or more candidate TNS
          license-holders. We rank highest-value customers first (most paid
          revenue). For each candidate, we show the signals that triggered the
          suggestion plus the license-holder&apos;s usage profile so you can
          pick the right account.
        </p>
        {groups.length === 0 ? (
          <div className={styles.emptyState}>
            No Shopify suggestions awaiting review.
          </div>
        ) : (
          <MatchGroupList
            groups={groups}
            onChange={() => {
              load();
              onLinkChange();
            }}
            platform="shopify"
          />
        )}
      </div>
    </div>
  );
};

// ============================================================
// HubSpot tab: stats cards, lifecycle/deal-stage breakdown, match review
// ============================================================
const HubspotTab: React.FC<{ onLinkChange: () => void }> = ({
  onLinkChange,
}) => {
  const [stats, setStats] = useState<HubspotPlatformStats | null>(null);
  const [groups, setGroups] = useState<MatchGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g] = await Promise.all([
        APIService.ADMIN_ANALYTICS.GET_HUBSPOT_STATS(),
        APIService.ADMIN_ANALYTICS.GET_SYNC_GROUPS("hubspot", 50, 0),
      ]);
      setStats(s);
      setGroups(g.groups);
    } catch (e) {
      console.error("Failed to load HubSpot tab", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className={styles.card}>Loading HubSpot data…</div>;

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {stats && (
        <>
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>HubSpot At a Glance</h3>
            <StatGrid
              stats={[
                ["Total contacts", stats.totalContacts.toLocaleString()],
                ["Total companies", stats.totalCompanies.toLocaleString()],
                ["Total deals", stats.totalDeals.toLocaleString()],
                [
                  "Linked to TNS",
                  `${stats.contactsLinkedToTNS.toLocaleString()} (${
                    stats.contactsLinkedPercent
                  }%)`,
                  "#4CAF50",
                ],
                ["Closed-won", fmtMoney(stats.closedWonValue), "#4CAF50"],
                ["Open pipeline", fmtMoney(stats.openPipelineValue), "#FFC107"],
              ]}
            />
          </div>

          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Contacts by Lifecycle Stage</h3>
            <DistributionList
              rows={stats.contactsByLifecycleStage.map((r) => ({
                label: r.stage,
                count: r.count,
              }))}
            />
          </div>

          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Deals by Stage</h3>
            <DistributionList
              rows={stats.dealsByStage.map((r) => ({
                label: r.stage,
                count: r.count,
                amount: r.amount,
              }))}
            />
          </div>
        </>
      )}

      <div className={styles.card}>
        <h3 className={styles.cardTitle}>
          Likely matches awaiting your review ({groups.length})
        </h3>
        <p className={styles.helpText}>
          Each row is a HubSpot contact with one or more candidate TNS
          license-holders. Sorted by match confidence; the signals that
          triggered the match are shown next to each candidate alongside the
          license-holder&apos;s usage profile.
        </p>
        {groups.length === 0 ? (
          <div className={styles.emptyState}>
            No HubSpot suggestions awaiting review.
          </div>
        ) : (
          <MatchGroupList
            groups={groups}
            onChange={() => {
              load();
              onLinkChange();
            }}
            platform="hubspot"
          />
        )}
      </div>
    </div>
  );
};

// ============================================================
// Match group list — shared between Shopify and HubSpot tabs
// ============================================================
const MatchGroupList: React.FC<{
  groups: MatchGroup[];
  onChange: () => void;
  platform: "shopify" | "hubspot";
}> = ({ groups, onChange, platform }) => {
  return (
    <div style={{ display: "grid", gap: 16, marginTop: 12 }}>
      {groups.map((g) => (
        <MatchGroupCard
          key={g.sourceId}
          group={g}
          onChange={onChange}
          platform={platform}
        />
      ))}
    </div>
  );
};

const MatchGroupCard: React.FC<{
  group: MatchGroup;
  onChange: () => void;
  platform: "shopify" | "hubspot";
}> = ({ group, onChange, platform }) => {
  const [busyLinkId, setBusyLinkId] = useState<string | null>(null);
  const history = useHistory();

  const decide = async (linkId: string, action: "approve" | "reject") => {
    setBusyLinkId(linkId);
    try {
      if (action === "approve") {
        await APIService.ADMIN_ANALYTICS.APPROVE_LINK(linkId);
      } else {
        await APIService.ADMIN_ANALYTICS.REJECT_LINK(linkId);
      }
      onChange();
    } catch (e) {
      console.error(`Failed to ${action} link`, e);
    } finally {
      setBusyLinkId(null);
    }
  };

  // When the admin approves one candidate, mark the rest of this group's
  // candidates as rejected — Shopify customer / HubSpot contact can only
  // map to one TNS license-holder.
  const approveAndRejectRest = async (chosenLinkId: string) => {
    setBusyLinkId(chosenLinkId);
    try {
      await APIService.ADMIN_ANALYTICS.APPROVE_LINK(chosenLinkId);
      const others = group.candidates.filter((c) => c.linkId !== chosenLinkId);
      await Promise.all(
        others.map((c) =>
          APIService.ADMIN_ANALYTICS.REJECT_LINK(c.linkId).catch((e) =>
            console.error("Failed to reject sibling", e)
          )
        )
      );
      onChange();
    } catch (e) {
      console.error("Approve+reject-others failed", e);
    } finally {
      setBusyLinkId(null);
    }
  };

  const ext = group.external;
  const showPaid =
    platform === "shopify" &&
    typeof ext.paidTotal === "number" &&
    ext.paidTotal > 0;

  return (
    <div
      style={{
        border: "1px solid #424258",
        borderRadius: 6,
        background: "#202234",
        overflow: "hidden",
      }}
    >
      {/* External record header */}
      <div
        style={{
          padding: 14,
          borderBottom: "1px solid #424258",
          background: "#262838",
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "center",
        }}
      >
        <div style={{ flex: 1, minWidth: 240 }}>
          <div
            style={{
              fontSize: 11,
              color: "#ababb7",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            {platform === "shopify" ? "Shopify customer" : "HubSpot contact"}
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#fff" }}>
            {ext.name ?? ext.email ?? "(no name)"}
          </div>
          <div style={{ fontSize: 13, color: "#ababb7", marginTop: 2 }}>
            {ext.email ?? "—"}
            {ext.phone ? ` · ${ext.phone}` : ""}
          </div>
          {(ext.company || ext.city || ext.state) && (
            <div style={{ fontSize: 13, color: "#ababb7", marginTop: 2 }}>
              {[ext.company, [ext.city, ext.state].filter(Boolean).join(", ")]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
          {ext.lifecycleStage && (
            <div style={{ fontSize: 12, color: "#ffbc17", marginTop: 4 }}>
              Lifecycle: {ext.lifecycleStage}
            </div>
          )}
        </div>
        {showPaid && (
          <div
            style={{
              padding: "8px 14px",
              borderRadius: 6,
              background: "rgba(76,175,80,0.15)",
              color: "#4CAF50",
              fontSize: 14,
              fontWeight: 600,
              textAlign: "right",
            }}
          >
            <div style={{ fontSize: 18 }}>{fmtMoney(ext.paidTotal!, 0)}</div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>
              {ext.paidOrderCount} paid order
              {ext.paidOrderCount === 1 ? "" : "s"}
            </div>
          </div>
        )}
      </div>

      {/* Candidate rows */}
      <div>
        {group.candidates.map((c, idx) => (
          <CandidateRow
            key={c.linkId}
            candidate={c}
            isFirst={idx === 0}
            busy={busyLinkId === c.linkId}
            onApprove={() => approveAndRejectRest(c.linkId)}
            onReject={() => decide(c.linkId, "reject")}
            onOpenProfile={() =>
              history.push(
                `/admin/customer/${encodeURIComponent(c.licenseEmail)}`
              )
            }
          />
        ))}
      </div>
    </div>
  );
};

const CandidateRow: React.FC<{
  candidate: MatchCandidate;
  isFirst: boolean;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  onOpenProfile: () => void;
}> = ({ candidate, isFirst, busy, onApprove, onReject, onOpenProfile }) => {
  const c = candidate;
  const matchColor =
    c.matchPercent >= 80
      ? "#4CAF50"
      : c.matchPercent >= 50
      ? "#FFC107"
      : "#FF7043";

  return (
    <div
      style={{
        padding: 14,
        borderTop: isFirst ? "none" : "1px solid #2f3142",
        display: "grid",
        gridTemplateColumns: "60px 1fr auto",
        gap: 16,
        alignItems: "center",
      }}
    >
      {/* % pill */}
      <div
        style={{
          background: `${matchColor}22`,
          color: matchColor,
          fontSize: 18,
          fontWeight: 700,
          padding: "12px 4px",
          borderRadius: 6,
          textAlign: "center",
          border: `1px solid ${matchColor}55`,
        }}
        title="Match likelihood"
      >
        {c.matchPercent}%
      </div>

      {/* TNS license-holder details */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>
          {c.license.name ?? c.licenseEmail}
        </div>
        <div style={{ fontSize: 12, color: "#ababb7", marginTop: 2 }}>
          {c.licenseEmail}
        </div>
        {c.license.teamName && (
          <div style={{ fontSize: 12, color: "#ababb7", marginTop: 2 }}>
            {c.license.teamName}
            {c.license.teamLevel ? ` · ${c.license.teamLevel}` : ""}
            {c.license.city || c.license.state
              ? ` · ${[c.license.city, c.license.state]
                  .filter(Boolean)
                  .join(", ")}`
              : ""}
          </div>
        )}
        {/* Match-reason chips */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            marginTop: 6,
          }}
        >
          {c.matchReason.split(/;\s*/).map((part, i) => (
            <span
              key={i}
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 999,
                background: "rgba(255,188,23,0.1)",
                color: "#ffbc17",
              }}
            >
              {part}
            </span>
          ))}
        </div>
        {/* Usage stats — the deciding signals when picking among candidates */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 14,
            marginTop: 8,
            fontSize: 12,
            color: "#ababb7",
          }}
        >
          <UsageChip
            label="Questions"
            value={c.license.questionsAllTime.toLocaleString()}
            highlight={c.license.questionsAllTime > 0}
          />
          <UsageChip
            label="Users"
            value={c.license.totalUsers.toLocaleString()}
          />
          <UsageChip
            label="Created"
            value={fmtRelDate(c.license.dateCreated)}
          />
          <UsageChip
            label="Last coach"
            value={fmtRelDate(c.license.lastCoachActivityAt)}
            highlight={!!c.license.lastCoachActivityAt}
          />
          <UsageChip
            label="Last player"
            value={fmtRelDate(c.license.lastPlayerActivityAt)}
            highlight={!!c.license.lastPlayerActivityAt}
          />
        </div>
        <button
          type="button"
          onClick={onOpenProfile}
          style={{
            marginTop: 8,
            background: "transparent",
            border: "none",
            color: "#ffbc17",
            fontSize: 12,
            cursor: "pointer",
            textDecoration: "underline",
            padding: 0,
          }}
        >
          Open profile →
        </button>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8 }}>
        <Button
          theme="primary"
          size="small"
          onClick={onApprove}
          disabled={busy}
        >
          This is the one
        </Button>
        <Button
          theme="tertiary"
          size="small"
          onClick={onReject}
          disabled={busy}
        >
          Not a match
        </Button>
      </div>
    </div>
  );
};

// ============================================================
// Helpers / sub-components
// ============================================================

const StatGrid: React.FC<{
  stats: Array<[string, React.ReactNode, string?]>;
}> = ({ stats }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
      gap: 16,
    }}
  >
    {stats.map(([label, value, color], i) => (
      <div key={i}>
        <div style={{ fontSize: 11, color: "#ababb7", marginBottom: 4 }}>
          {label}
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: color ?? "#fff",
          }}
        >
          {value}
        </div>
      </div>
    ))}
  </div>
);

const UsageChip: React.FC<{
  label: string;
  value: string;
  highlight?: boolean;
}> = ({ label, value, highlight }) => (
  <div>
    <span style={{ opacity: 0.7 }}>{label}: </span>
    <strong
      style={{
        color: highlight ? "#fff" : "#ababb7",
      }}
    >
      {value}
    </strong>
  </div>
);

const DistributionList: React.FC<{
  rows: { label: string; count: number; amount?: number }[];
}> = ({ rows }) => {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {rows.map((r) => (
        <div
          key={r.label}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 60px 100px",
            gap: 10,
            alignItems: "center",
            fontSize: 13,
          }}
        >
          <div style={{ color: "#fff" }}>{r.label}</div>
          <div
            style={{
              height: 6,
              background: "#2a2c3a",
              borderRadius: 3,
              overflow: "hidden",
              gridColumn: "2 / 4",
              position: "relative",
            }}
          >
            <div
              style={{
                width: `${(r.count / max) * 100}%`,
                height: "100%",
                background: "#ffbc17",
              }}
            />
          </div>
          <div style={{ color: "#ababb7", textAlign: "right" }}>
            {r.count.toLocaleString()}
            {typeof r.amount === "number" && r.amount > 0
              ? ` · ${fmtMoney(r.amount, 0)}`
              : ""}
          </div>
        </div>
      ))}
    </div>
  );
};

const MonthlyRevenueBar: React.FC<{
  data: { month: string; revenue: number; orderCount: number }[];
}> = ({ data }) => {
  if (data.length === 0) {
    return <p className={styles.emptyState}>No monthly revenue data yet.</p>;
  }
  const chartData = data.map((d) => ({
    month: d.month,
    revenue: d.revenue,
  }));
  return (
    <div style={{ height: 320 }}>
      <ResponsiveBar
        data={chartData}
        keys={["revenue"]}
        indexBy="month"
        margin={{ top: 10, right: 20, bottom: 60, left: 70 }}
        padding={0.25}
        colors={["#ffbc17"]}
        borderRadius={2}
        axisBottom={{
          tickRotation: -45,
          tickSize: 5,
          tickPadding: 5,
          format: (v) =>
            typeof v === "string" && v.length === 7 ? v : String(v),
        }}
        axisLeft={{
          format: (v) =>
            typeof v === "number"
              ? "$" + (v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`)
              : String(v),
        }}
        enableLabel={false}
        theme={{
          textColor: "#ababb7",
          fontSize: 11,
          axis: {
            ticks: { text: { fill: "#ababb7", fontSize: 10 } },
          },
          grid: { line: { stroke: "#2f3142", strokeWidth: 1 } },
          tooltip: {
            container: {
              background: "#282B3A",
              color: "#fff",
              fontSize: 12,
              borderRadius: 4,
            },
          },
        }}
        tooltip={({ value, indexValue }) => (
          <div
            style={{
              background: "#282B3A",
              padding: 8,
              borderRadius: 4,
              fontSize: 12,
            }}
          >
            <strong>{indexValue}</strong>: {fmtMoney(Number(value))}
          </div>
        )}
      />
    </div>
  );
};

function fmtMoney(n: number, fractionDigits = 0): string {
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
}

function fmtRelDate(s: string | null): string {
  if (!s) return "never";
  const d = DateTime.fromISO(s);
  if (!d.isValid) return "—";
  const now = DateTime.now();
  const diff = now.diff(d, "days").days;
  if (diff < 1) return "today";
  if (diff < 7) return `${Math.floor(diff)}d ago`;
  if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
  return `${Math.floor(diff / 365)}y ago`;
}

export default AdminImport;
